import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
    CreateQrdaExportRunRequest,
    CreateSinglePatientRunRequest,
    QrdaExportClient,
    QrdaExportRunsSearchDto,
    QrdaPackagingOptions,
} from '@medone/medonehp-api-client';

import { handleError } from '../../../shared/common/HandleErrors';
import { Axios } from '../../../shared/common/http';
import { RootState } from '../../../shared/store';
import { initialQualityReportingState, QualityReportingState } from './models';

const buildClient = () => new QrdaExportClient(null as any, Axios);

export const fetchCqmMeasures = createAsyncThunk(
    'qualityReporting/fetchCqmMeasures',
    async () => {
        const response = await buildClient().getCqmMeasures();
        if (response.result.succeeded) {
            return response.result.entity ?? [];
        }
        handleError(response, () => {});
        return [];
    },
);

export const fetchRuns = createAsyncThunk(
    'qualityReporting/fetchRuns',
    async (search: Partial<QrdaExportRunsSearchDto> & { current?: number; pageSize?: number }) => {
        const dto = QrdaExportRunsSearchDto.fromJS({
            current: search.current ?? 1,
            pageSize: search.pageSize ?? 50,
            status: search.status,
            patientId: search.patientId,
        });
        const response = await buildClient().getRuns(dto);
        if (response.result.succeeded) {
            return {
                items: response.result.entity ?? [],
                total: response.result.pagination?.total ?? response.result.entity?.length ?? 0,
            };
        }
        handleError(response, () => {});
        return { items: [], total: 0 };
    },
);

export const fetchRun = createAsyncThunk(
    'qualityReporting/fetchRun',
    async (id: number) => {
        const response = await buildClient().getRun(id);
        if (response.result.succeeded) {
            return response.result.entity ?? null;
        }
        handleError(response, () => {});
        return null;
    },
);

export const createRun = createAsyncThunk(
    'qualityReporting/createRun',
    async (req: CreateQrdaExportRunRequest) => {
        const response = await buildClient().createRun(req);
        if (response.result.succeeded) {
            return response.result.entity!;
        }
        handleError(response, () => {});
        throw new Error(response.result.message || 'Failed to create run');
    },
);

export const createSinglePatientRun = createAsyncThunk(
    'qualityReporting/createSinglePatientRun',
    async (req: CreateSinglePatientRunRequest) => {
        const response = await buildClient().createSinglePatientRun(req);
        if (response.result.succeeded) {
            return response.result.entity!;
        }
        handleError(response, () => {});
        throw new Error(response.result.message || 'Failed to create single-patient run');
    },
);

/**
 * Re-publishes the Service Bus message for a stuck `NotStarted` run. Backend enforces:
 * status must be NotStarted AND createdDate is at least 1 minute old. Returns the fresh DTO
 * on success; throws on 404 / 409 / network error. Idempotent — clicking multiple times just
 * enqueues redundant messages that the consumer dedupes via patient-level status checks.
 */
export const retriggerRun = createAsyncThunk(
    'qualityReporting/retriggerRun',
    async (id: number) => {
        const response = await buildClient().retrigger(id);
        if (response.result.succeeded) {
            return response.result.entity!;
        }
        handleError(response, () => {});
        throw new Error(response.result.message || 'Failed to retrigger run');
    },
);

/**
 * Per-measure file counts for a run. Fetched when the download menu opens rather than with the run
 * list, so the list screen keeps one request per page instead of one per run.
 */
export const fetchMeasurePackaging = createAsyncThunk(
    'qualityReporting/fetchMeasurePackaging',
    async (id: number) => {
        const response = await buildClient().measurePackaging(id);
        if (response.result.succeeded) {
            return response.result.entity ?? [];
        }
        handleError(response, () => {});
        return [];
    },
);

export interface DownloadRunArgs {
    id: number;
    /** Defaults to the combined ZIP, which is what every caller before SCRUM-1311 wants. */
    packaging?: QrdaPackagingOptions;
    /** Required by, and only read for, the per-measure option. */
    measureCmsId?: string;
}

/**
 * The API explains a refused packaging shape in `ProblemDetails.title` — which measure has no computed
 * results, or that the archive is too large to repack. The download request is `responseType: blob`, so
 * that body arrives as a Blob rather than a string. The (c)(1) runbook tells the operator to act on this
 * message, so it must not be swallowed.
 */
const readErrorBody = (body: unknown): Promise<string | null> => {
    if (typeof body === 'string') return Promise.resolve(body);

    // Blob.text() is what browsers give us. jsdom's Blob has neither text() nor arrayBuffer(), so the
    // test path needs FileReader — which is also the fallback for older browsers.
    const readable = body as { text?: () => Promise<string> } | null;
    if (typeof readable?.text === 'function') return readable.text();

    if (typeof FileReader === 'undefined' || !(body instanceof Blob)) return Promise.resolve(null);

    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
        reader.onerror = () => resolve(null);
        reader.readAsText(body);
    });
};

const problemDetailsTitle = async (error: unknown): Promise<string | null> => {
    try {
        const text = await readErrorBody((error as { response?: unknown })?.response);
        const parsed = text ? JSON.parse(text) : null;
        return typeof parsed?.title === 'string' ? parsed.title : null;
    } catch {
        return null;
    }
};

/**
 * Streams one of a completed run's ZIP shapes and forces a browser download. Mirrors the
 * blob + anchor-click pattern used by `shared/common/files/DownloadFile.tsx`.
 */
export const downloadRun = createAsyncThunk(
    'qualityReporting/downloadRun',
    async (arg: number | DownloadRunArgs) => {
        const { id, packaging, measureCmsId } = typeof arg === 'number' ? { id: arg } as DownloadRunArgs : arg;

        let file;
        try {
            file = (await buildClient().download(id, packaging, measureCmsId)).result;
        } catch (error) {
            throw new Error((await problemDetailsTitle(error)) ?? 'The download could not be produced.');
        }

        if (!file?.data) return;

        const contentType =
            (file.headers && (file.headers['content-type'] || file.headers['Content-Type'])) ||
            'application/zip';
        const blob = new Blob([file.data], { type: contentType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', file.fileName || `qrda-export-${id}.zip`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    },
);

const slice = createSlice({
    name: 'qualityReporting',
    initialState: initialQualityReportingState,
    reducers: {
        clearCurrentRun: (state: QualityReportingState) => {
            state.currentRun = null;
        },
        setError: (state: QualityReportingState, action: PayloadAction<string | null>) => {
            state.errorMessage = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCqmMeasures.fulfilled, (state, action) => {
                state.cqmMeasures = action.payload;
            })
            .addCase(fetchRuns.pending, (state) => {
                state.loading = true;
                state.errorMessage = null;
            })
            .addCase(fetchRuns.fulfilled, (state, action) => {
                state.loading = false;
                state.runs = action.payload.items;
                state.runsTotal = action.payload.total;
            })
            .addCase(fetchRuns.rejected, (state, action) => {
                state.loading = false;
                state.errorMessage = action.error.message ?? 'Failed to load runs.';
            })
            .addCase(fetchRun.pending, (state) => {
                state.loading = true;
                state.errorMessage = null;
            })
            .addCase(fetchRun.fulfilled, (state, action) => {
                state.loading = false;
                state.currentRun = action.payload;
            })
            .addCase(fetchRun.rejected, (state, action) => {
                state.loading = false;
                state.errorMessage = action.error.message ?? 'Failed to load run.';
            })
            .addCase(createRun.pending, (state) => {
                state.processing = true;
                state.errorMessage = null;
            })
            .addCase(createRun.fulfilled, (state, action) => {
                state.processing = false;
                state.currentRun = action.payload;
            })
            .addCase(createRun.rejected, (state, action) => {
                state.processing = false;
                state.errorMessage = action.error.message ?? 'Failed to create run.';
            })
            .addCase(createSinglePatientRun.fulfilled, (state, action) => {
                state.currentRun = action.payload;
            });
    },
});

export const { clearCurrentRun, setError } = slice.actions;

export const selectRuns = (state: RootState) => (state as any).qualityReporting?.runs ?? [];
export const selectRunsTotal = (state: RootState) => (state as any).qualityReporting?.runsTotal ?? 0;
export const selectCurrentRun = (state: RootState) => (state as any).qualityReporting?.currentRun ?? null;
export const selectMeasures = (state: RootState) => (state as any).qualityReporting?.cqmMeasures ?? [];
export const selectLoading = (state: RootState) => (state as any).qualityReporting?.loading ?? false;
export const selectProcessing = (state: RootState) => (state as any).qualityReporting?.processing ?? false;
export const selectError = (state: RootState) => (state as any).qualityReporting?.errorMessage ?? null;

export default slice.reducer;
